"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";

const PRIVATE_PATHS = ["/portfolio", "/alerts", "/trade-journal", "/admin"];

function isPrivatePath(pathname: string) {
  return PRIVATE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export default function AuthGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isLoggedIn, isReady } = useAuth();
  const [ready, setReady] = useState(false);

  const privatePath = useMemo(() => (pathname ? isPrivatePath(pathname) : false), [pathname]);

  useEffect(() => {
    const markReady = window.setTimeout(() => setReady(true), 0);
    return () => window.clearTimeout(markReady);
  }, []);

  useEffect(() => {
    if (!ready || !isReady || !privatePath || isLoggedIn) return;
    router.replace("/login");
  }, [isLoggedIn, isReady, privatePath, ready, router]);

  // Server-rendered output must always contain the page content. `ready` is only
  // set from an effect, so bailing out before hydration ships an empty <body> to
  // crawlers and makes every route look like thin content. Gating is a client
  // concern — apply it once we actually know the auth state.
  if (ready && privatePath && isReady && !isLoggedIn) return null;

  return <>{children}</>;
}
